import { type ComponentProps, useState } from 'react';

import { cn } from '../lib/cn';

/** Seis acentos (`--company-*`), elegidos por hash del nombre/slug: estables entre pantallas. */
const ACCENTS = [
  'bg-company-1/14 text-company-1 ring-company-1/25',
  'bg-company-2/14 text-company-2 ring-company-2/25',
  'bg-company-3/14 text-company-3 ring-company-3/25',
  'bg-company-4/14 text-company-4 ring-company-4/25',
  'bg-company-5/14 text-company-5 ring-company-5/25',
  'bg-company-6/14 text-company-6 ring-company-6/25',
] as const;

const DOTS = [
  'bg-company-1',
  'bg-company-2',
  'bg-company-3',
  'bg-company-4',
  'bg-company-5',
  'bg-company-6',
] as const;

export function accentIndex(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h % ACCENTS.length;
}

/** Iniciales: "Sweet Papaya" → "SP", "Central ETW" → "CE", "starsam" → "ST". */
export function initials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  const [first = '', second = ''] = words;
  const letters = second ? `${first[0] ?? ''}${second[0] ?? ''}` : first.slice(0, 2);
  return letters.toUpperCase() || '·';
}

export interface MonogramProps extends ComponentProps<'span'> {
  name: string;
  /** Clave del color (el slug, si existe); por defecto el nombre. */
  colorKey?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /**
   * Logo real (favicon de la web, logo subido, foto de perfil). Mientras carga se ven las iniciales;
   * si falla (404, imagen rota) se quedan las iniciales: nunca un ícono de imagen rota.
   */
  src?: string | null | undefined;
  /** `photo` (foto de perfil) llena el recuadro; `logo` respira dentro de un fondo blanco. */
  kind?: 'logo' | 'photo';
}

/** Avatar con el logo (o las iniciales, con el acento propio de cada empresa o persona). Decorativo. */
export function Monogram({
  name,
  colorKey,
  size = 'md',
  src,
  kind = 'logo',
  className,
  ...props
}: MonogramProps) {
  const [loaded, setLoaded] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const showImg = Boolean(src) && failed !== src;
  const visible = showImg && loaded === src;
  return (
    <span
      aria-hidden
      data-logo={visible ? 'image' : 'initials'}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-lg font-display font-semibold tracking-tight ring-1 ring-inset select-none',
        visible ? 'bg-white ring-foreground/10' : ACCENTS[accentIndex(colorKey ?? name)],
        size === 'xs' && 'size-5 rounded-md text-[0.6rem]',
        size === 'sm' && 'size-7 text-[0.7rem]',
        size === 'md' && 'size-9 text-xs',
        size === 'lg' && 'size-11 text-sm',
        className,
      )}
      {...props}
    >
      <span className={cn(visible && 'invisible')}>{initials(name)}</span>
      {showImg && src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={() => {
            setLoaded(src);
          }}
          onError={() => {
            setFailed(src);
          }}
          className={cn(
            'absolute inset-0 size-full object-contain transition-opacity duration-200',
            kind === 'photo' ? 'object-cover' : 'p-[14%]',
            visible ? 'opacity-100' : 'opacity-0',
          )}
        />
      ) : null}
    </span>
  );
}

/** Punto de color de la empresa (para chips y leyendas compactas). */
export function AccentDot({ colorKey, className }: { colorKey: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('size-2 shrink-0 rounded-full', DOTS[accentIndex(colorKey)], className)}
    />
  );
}
